import { useCallback, useEffect, useMemo, useState } from 'react';
import { localeUI } from '../lib/format';
import { Calendar, Check, DollarSign, Eye, EyeOff, FileText, Mail, MessageSquare, Pause, Phone, Play, RefreshCw, Send, Sparkles, StickyNote, Trash2, X } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { useAuth, hasRole } from '../lib/auth';

/**
 * HO UPDATES — la pestaña del claim.
 *
 * Arriba, la semana en curso: el bucket EN VIVO (la misma consulta que va a leer la IA el viernes),
 * con "no incluir" por ítem y la nota para el update. Abajo, los updates de cada semana con su
 * texto editable, las razones por las que pide revisión, y el rastreo: generado → revisado →
 * enviado → entregado → abierto → link visto.
 */
export type HoUpdate = {
  id: string; claim_id: string; period_start: string; period_end: string; period_key: string; language: string;
  status: 'draft' | 'held' | 'approved' | 'skipped' | 'sent' | 'failed' | 'superseded';
  needs_review: boolean; review_reasons: string[]; quiet_week: boolean; manual: boolean;
  headline: string | null; text_ai: string | null; text_final: string | null; next_step: string | null;
  omitted: Array<{ item: string; why: string }>; sources: string[];
  model: string | null; tokens_in: number | null; tokens_out: number | null; generated_at: string | null; generation_error: string | null;
  reviewed_by: string | null; reviewed_at: string | null; edited_by: string | null; edited_at: string | null; skipped_by: string | null; skipped_at: string | null;
  sent_at: string | null; email_status: string | null; sms_status: string | null; send_error: string | null;
  email_delivered_at: string | null; email_opened_at: string | null; email_clicked_at: string | null;
  first_viewed_at: string | null; last_viewed_at: string | null; view_count: number; viewed_via: string | null;
  /** rojo / amarillo / verde / gris — lo decide el backend con una sola fórmula (SEMAFORO_SQL). */
  semaforo?: 'red' | 'yellow' | 'green' | 'gray';
};
type Resp = {
  updates: HoUpdate[];
  claim: { homeowner_name: string; homeowner_email: string | null; phone: string | null; language: string; preferred_language: string | null; lifecycle_status: string | null; status_days: number | null; contractor_name: string | null; paused_at: string | null; paused_by: string | null; pause_reason: string | null };
  link: { token: string; revoked: boolean; hits: number; last_seen_at: string | null } | null;
  config: { enabled: boolean; mode: 'dry_run' | 'pilot' | 'all'; review_policy: string; period_key: string; generate: string; send: string };
};
type Item = { key: string; kind: string; at: string; dept: string | null; direction: 'in' | 'out' | null; who: string; who_name: string | null; title: string | null; text: string; excluded: boolean; amount_cents: number | null };
type Bucket = { desde: string; hasta: string; period_key: string; items: Item[]; notas: Array<{ id: string; body: string; author: string | null; created_at: string }>; hechos: { stage_phrase: string; waiting_on: string | null; next_appointment: string | null; account_manager: string; previous_update: string | null } };

/** Los colores del semáforo: estado (--red/--orange/--green), reservados para eso. */
export const SEMAFORO: Record<'red' | 'yellow' | 'green' | 'gray', { color: string; soft: string }> = {
  red: { color: 'var(--red)', soft: 'var(--red-soft)' },
  yellow: { color: 'var(--orange)', soft: 'var(--orange-soft)' },
  green: { color: 'var(--green)', soft: 'var(--green-soft)' },
  gray: { color: 'var(--muted)', soft: 'var(--surface-2)' },
};

const ICONO: Record<string, typeof Mail> = { note: StickyNote, msg: FileText, email: Mail, sms: MessageSquare, call: Phone, status: RefreshCw, appt: Calendar, check: DollarSign, supp: FileText, coc: FileText };
const COLOR: Record<string, string> = { note: 'var(--accent)', msg: 'var(--muted)', email: '#2563eb', sms: 'var(--green)', call: '#7e22ce', status: 'var(--orange)', appt: 'var(--accent)', check: 'var(--green)', supp: 'var(--orange)', coc: 'var(--orange)' };

/** Un 'YYYY-MM-DD' (una fecha de calendario, sin hora) se arma como fecha LOCAL: `new Date('2026-09-11')`
 *  la toma como medianoche UTC y en el Este ya es el día anterior — la trampa de siempre con las columnas date. */
export const fechaDia = (ymd: string) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(ymd);
  return d.toLocaleDateString(localeUI(), { month: 'short', day: 'numeric' });
};
const fecha = (s: string | null | undefined, conHora = true) => s ? (/^\d{4}-\d{2}-\d{2}$/.test(s) ? fechaDia(s) : new Date(s).toLocaleString(localeUI(), { month: 'short', day: 'numeric', ...(conHora ? { hour: 'numeric', minute: '2-digit' } : {}) })) : '';
const diaDe = (s: string) => new Date(s).toLocaleDateString(localeUI(), { weekday: 'short', month: 'short', day: 'numeric' });

export function estadoPill(status: HoUpdate['status'], t: (k: string) => string): { label: string; cls: string } {
  switch (status) {
    case 'sent': return { label: t('hou.st_sent'), cls: 'green' };
    case 'approved': return { label: t('hou.st_approved'), cls: 'blue' };
    case 'held': return { label: t('hou.st_held'), cls: 'orange' };
    case 'skipped': return { label: t('hou.st_skipped'), cls: 'gray' };
    case 'failed': return { label: t('hou.st_failed'), cls: 'red' };
    case 'superseded': return { label: t('hou.st_superseded'), cls: 'gray' };
    default: return { label: t('hou.st_draft'), cls: 'gray' };
  }
}

/** El rastreo de un update: cada paso con su hora, como el tracker de los correos. */
export function Rastreo({ u, t }: { u: HoUpdate; t: (k: string) => string }) {
  const pasos: Array<{ k: string; label: string; at: string | null; done: boolean }> = [
    { k: 'gen', label: t('hou.tr_generated'), at: u.generated_at, done: Boolean(u.generated_at) },
    { k: 'rev', label: u.reviewed_at ? `${t('hou.tr_reviewed')} · ${u.reviewed_by ?? ''}` : t('hou.tr_notReviewed'), at: u.reviewed_at, done: Boolean(u.reviewed_at) },
    { k: 'sent', label: t('hou.tr_sent'), at: u.sent_at, done: u.status === 'sent' },
  ];
  if (u.status === 'sent') {
    pasos.push({ k: 'email', label: `${t('hou.tr_email')} · ${u.email_status ?? '—'}`, at: null, done: ['sent', 'delivered', 'opened', 'clicked'].includes(u.email_status ?? '') });
    if (u.email_delivered_at) pasos.push({ k: 'del', label: t('hou.tr_delivered'), at: u.email_delivered_at, done: true });
    pasos.push({ k: 'open', label: u.email_opened_at ? t('hou.tr_opened') : t('hou.tr_notOpened'), at: u.email_opened_at, done: Boolean(u.email_opened_at) });
    pasos.push({ k: 'sms', label: `${t('hou.tr_sms')} · ${u.sms_status ?? '—'}`, at: null, done: ['sent', 'queued'].includes(u.sms_status ?? '') });
    pasos.push({ k: 'view', label: u.first_viewed_at ? `${t('hou.tr_viewed')} (${u.viewed_via === 'sms' ? 'SMS' : u.viewed_via === 'email' ? 'email' : 'link'}) ×${u.view_count}` : t('hou.tr_notViewed'), at: u.first_viewed_at, done: Boolean(u.first_viewed_at) });
  }
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
      {pasos.map((p, i) => (
        <span key={p.k} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 'var(--fs-2xs)', color: p.done ? 'var(--ink)' : 'var(--muted)' }}>
          {i > 0 && <span style={{ width: 10, height: 1, background: p.done ? 'var(--accent)' : 'var(--line)' }} />}
          <span style={{ width: 14, height: 14, borderRadius: '50%', display: 'grid', placeItems: 'center', background: p.done ? 'var(--accent-soft)' : 'var(--surface-2)', color: p.done ? 'var(--accent)' : 'var(--muted)' }}>
            {p.done ? <Check size={9} strokeWidth={3} /> : null}
          </span>
          {p.label}{p.at ? <span className="muted"> · {fecha(p.at)}</span> : null}
        </span>
      ))}
    </div>
  );
}

const RAZON: Record<string, string> = {
  cifra_no_verificada: 'hou.r_cifra', monto_en_texto: 'hou.r_monto', muy_largo: 'hou.r_largo', muy_corto: 'hou.r_corto', idioma: 'hou.r_idioma', etapa_sensible: 'hou.r_etapa', ia_lo_pidio: 'hou.r_ia',
};

/** Una tarjeta de update, compartida con la cola. */
export type ClaimInfo = { contractor: string | null; status: string | null; statusDays: number | null };

/**
 * LA VENTANA DE LOS ÍTEMS DE LA SEMANA — lo que alimentó (o va a alimentar) este update, con una
 * casilla por ítem. Desmarcar = "no incluir" (la misma exclusión de la pestaña), y "Regenerar con
 * estos ítems" vuelve a pedirle el texto a la IA sin lo que se sacó.
 */
function BucketModal({ u, t, onChanged, onClose }: { u: HoUpdate; t: (k: string, v?: Record<string, string | number>) => string; onChanged: (nuevo: HoUpdate) => void; onClose: () => void }) {
  const [bucket, setBucket] = useState<Bucket | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [abierto, setAbierto] = useState<string | null>(null);
  useEffect(() => {
    api.get<Bucket>(`/api/claims/${u.claim_id}/ho-updates/bucket`).then(setBucket).catch((e) => setError(e instanceof ApiError ? e.message : 'Error'));
  }, [u.claim_id]);
  async function toggle(it: Item) {
    try {
      await api.post(`/api/claims/${u.claim_id}/ho-updates/exclusions`, { item_key: it.key, excluded: !it.excluded });
      setBucket((b) => b ? { ...b, items: b.items.map((x) => x.key === it.key ? { ...x, excluded: !x.excluded } : x) } : b);
    } catch (e) { setError(e instanceof ApiError ? e.message : 'Error'); }
  }
  async function regenerar() {
    setBusy('regen'); setError(null);
    try { const r = await api.post<{ update: HoUpdate }>(`/api/ho-updates/${u.id}/regenerate`); if (r.update) onChanged(r.update); onClose(); }
    catch (e) { setError(e instanceof ApiError ? (e.code === 'claude_not_configured' ? t('hou.noClaude') : e.message) : 'Error'); } finally { setBusy(null); }
  }
  const activos = bucket?.items.filter((i) => !i.excluded).length ?? 0;
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(10,11,16,0.55)', display: 'grid', placeItems: 'center', padding: 16 }}>
      <div className="card" onClick={(e) => e.stopPropagation()} style={{ width: 'min(760px, 100%)', maxHeight: '86vh', display: 'grid', gridTemplateRows: 'auto 1fr auto', gap: 10, padding: 16 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <b style={{ fontSize: 'var(--fs-sm)' }}>{t('hou.itemsModalTitle')}</b>
          {bucket && <span className="badge gray" style={{ fontSize: 'var(--fs-2xs)' }}>{t('hou.itemsCount', { n: activos })}</span>}
          <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('hou.itemsModalHint')}</span>
          <span style={{ flex: 1 }} />
          <button className="btn ghost sm" onClick={onClose}><X size={14} strokeWidth={1.75} /></button>
        </div>
        <div style={{ overflowY: 'auto', display: 'grid', gap: 4, alignContent: 'start' }}>
          {error && <div style={{ color: 'var(--red)', fontSize: 'var(--fs-xs)' }}>{error}</div>}
          {!bucket && !error && <div className="muted" style={{ fontSize: 'var(--fs-xs)' }}>{t('common.loadingDots')}</div>}
          {bucket && bucket.items.length === 0 && <div className="muted" style={{ fontSize: 'var(--fs-xs)', padding: 10 }}>{t('hou.emptyWeek')}</div>}
          {bucket?.items.map((it) => {
            const Ic = ICONO[it.kind] ?? FileText;
            const open = abierto === it.key;
            return (
              <label key={it.key} style={{ display: 'flex', gap: 8, alignItems: 'start', padding: '6px 8px', borderRadius: 8, border: '1px solid var(--line)', background: it.excluded ? 'transparent' : 'var(--card)', opacity: it.excluded ? 0.55 : 1, cursor: 'pointer' }}>
                <input type="checkbox" checked={!it.excluded} onChange={() => toggle(it)} style={{ marginTop: 3 }} />
                <span style={{ color: COLOR[it.kind] ?? 'var(--muted)', paddingTop: 2 }}><Ic size={13} strokeWidth={1.75} /></span>
                <span style={{ flex: 1, minWidth: 0, fontSize: 'var(--fs-xs)' }} onClick={(e) => { e.preventDefault(); setAbierto(open ? null : it.key); }}>
                  <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{fecha(it.at)} · {it.kind === 'msg' ? `${it.dept ?? 'General'} · ` : ''}{it.who_name ?? t(`hou.who_${it.who}`)}{it.direction ? (it.direction === 'in' ? ' ↙' : ' ↗') : ''}</span>
                  {it.title && <span style={{ display: 'block', fontWeight: 'var(--fw-semibold)' }}>{it.title}</span>}
                  <span style={{ display: 'block', whiteSpace: open ? 'pre-wrap' : 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textDecoration: it.excluded ? 'line-through' : 'none' }}>{it.text}</span>
                </span>
              </label>
            );
          })}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('hou.itemsModalFoot')}</span>
          <span style={{ flex: 1 }} />
          <button className="btn ghost sm" onClick={onClose}>{t('hou.close')}</button>
          <button className="btn sm" disabled={busy != null || !bucket} onClick={regenerar}><RefreshCw size={13} strokeWidth={1.75} /> {busy === 'regen' ? t('hou.generating') : t('hou.regenWith')}</button>
        </div>
      </div>
    </div>
  );
}

export function UpdateCard({ u, t, onChanged, showClaim, claimLabel, claimId, claim }: {
  u: HoUpdate; t: (k: string, v?: Record<string, string | number>) => string; onChanged: (nuevo: HoUpdate) => void;
  showClaim?: boolean; claimLabel?: string; claimId?: string;
  /** Contratista, estado actual y días en ese estado: van en la cabecera, al lado del estado del update. */
  claim?: ClaimInfo;
}) {
  const { me } = useAuth();
  const [texto, setTexto] = useState(u.text_final ?? '');
  const [editando, setEditando] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [verNotas, setVerNotas] = useState(false);
  const [verItems, setVerItems] = useState(false);
  useEffect(() => { setTexto(u.text_final ?? ''); setEditando(false); }, [u.id, u.text_final]);
  const pill = estadoPill(u.status, t);
  const bloqueado = u.status === 'sent';

  async function accion(a: 'approve' | 'skip' | 'unskip') {
    setBusy(a); setMsg(null);
    try { const r = await api.patch<{ update: HoUpdate }>(`/api/ho-updates/${u.id}`, { action: a }); onChanged(r.update); }
    catch (e) { setMsg(e instanceof ApiError ? e.message : 'Error'); } finally { setBusy(null); }
  }
  async function guardar() {
    setBusy('save'); setMsg(null);
    try { const r = await api.patch<{ update: HoUpdate }>(`/api/ho-updates/${u.id}`, { text_final: texto }); onChanged(r.update); setEditando(false); setMsg(t('hou.saved')); }
    catch (e) { setMsg(e instanceof ApiError ? e.message : 'Error'); } finally { setBusy(null); }
  }
  async function regenerar() {
    if (!window.confirm(t('hou.regenConfirm'))) return;
    setBusy('regen'); setMsg(null);
    try { const r = await api.post<{ update: HoUpdate }>(`/api/ho-updates/${u.id}/regenerate`); if (r.update) onChanged(r.update); }
    catch (e) { setMsg(e instanceof ApiError ? e.message : 'Error'); } finally { setBusy(null); }
  }
  async function enviarAhora() {
    if (!window.confirm(t('hou.sendConfirm'))) return;
    setBusy('send'); setMsg(null);
    try {
      await api.post(`/api/ho-updates/${u.id}/send`);
      const r = await api.get<Resp>(`/api/claims/${u.claim_id}/ho-updates`);
      const nuevo = r.updates.find((x) => x.id === u.id); if (nuevo) onChanged(nuevo);
    } catch (e) {
      const code = e instanceof ApiError ? e.code : null;
      setMsg(code === 'dry_run' ? t('hou.dryRunBlocked') : (e instanceof ApiError ? e.message : 'Error'));
    } finally { setBusy(null); }
  }

  return (
    <div className="card" style={{ display: 'grid', gap: 8, borderLeft: `4px solid ${SEMAFORO[u.semaforo ?? 'gray'].color}` }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {showClaim && claimId && <a href={`/claims/${claimId}?tab=ho-updates`} style={{ fontWeight: 'var(--fw-bold)', fontSize: 'var(--fs-sm)' }}>{claimLabel}</a>}
        <b style={{ fontSize: 'var(--fs-sm)' }}>{t('hou.weekOf', { date: fecha(u.period_key, false) })}</b>
        {u.semaforo && (
          <span className="badge" style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)', background: SEMAFORO[u.semaforo].soft, color: SEMAFORO[u.semaforo].color }}>
            ● {t(`hou.sem_${u.semaforo}`)}
          </span>
        )}
        <span className={`badge ${pill.cls}`} style={{ fontSize: 'var(--fs-2xs)' }}>{pill.label}</span>
        {claim && (claim.contractor || claim.status) && (
          <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', fontSize: 'var(--fs-2xs)', color: 'var(--ink-2)', flexWrap: 'wrap' }}>
            {claim.contractor && <span className="badge gray" style={{ fontSize: 'var(--fs-2xs)' }} title={t('hou.contractor')}>🏗 {claim.contractor}</span>}
            {claim.status && (
              <span className="badge blue" style={{ fontSize: 'var(--fs-2xs)' }}>
                {claim.status}{claim.statusDays != null ? ` · ${t('hou.daysInStatus', { n: claim.statusDays })}` : ''}
              </span>
            )}
          </span>
        )}
        {!bloqueado && (
          <button className="btn ghost sm" style={{ fontSize: 'var(--fs-2xs)' }} onClick={() => setVerItems(true)} title={t('hou.itemsModalHint')}>
            <FileText size={12} strokeWidth={1.75} /> {t('hou.weekItems', { n: u.sources.length })}
          </button>
        )}
        {u.language === 'es' && <span className="badge gray" style={{ fontSize: 'var(--fs-2xs)' }}>ES</span>}
        {u.quiet_week && <span className="badge gray" style={{ fontSize: 'var(--fs-2xs)' }}>{t('hou.quietWeek')}</span>}
        <span style={{ flex: 1 }} />
        <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{fecha(u.period_start, false)} → {fecha(u.period_end, false)}</span>
      </div>
      {u.headline && <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-2)' }}><Sparkles size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom', color: 'var(--accent)' }} /> {u.headline}</div>}
      {u.needs_review && u.review_reasons.length > 0 && u.status !== 'sent' && (
        <div style={{ fontSize: 'var(--fs-2xs)', color: 'var(--orange)', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {u.review_reasons.map((r, i) => <span key={i} className="badge orange" style={{ fontSize: 'var(--fs-2xs)' }}>{RAZON[r] ? t(RAZON[r]!) : r}</span>)}
        </div>
      )}
      {u.generation_error && <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--red)' }}>{u.generation_error === 'no_contact' ? t('hou.noContact') : u.generation_error}</div>}
      {u.send_error && <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--red)' }}>{u.send_error}</div>}

      {(u.text_final || u.text_ai) && (
        editando && !bloqueado ? (
          <textarea className="input" rows={9} value={texto} onChange={(e) => setTexto(e.target.value)} style={{ font: 'inherit', fontSize: 'var(--fs-sm)', lineHeight: 1.5, resize: 'vertical' }} />
        ) : (
          <div onClick={() => !bloqueado && setEditando(true)} title={bloqueado ? undefined : t('hou.clickToEdit')}
            style={{ whiteSpace: 'pre-wrap', fontSize: 'var(--fs-sm)', lineHeight: 1.55, padding: '10px 12px', borderRadius: 9, background: 'var(--surface-2)', cursor: bloqueado ? 'default' : 'text' }}>
            {u.text_final}
          </div>
        )
      )}
      {u.edited_at && <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('hou.editedBy', { name: u.edited_by ?? '', when: fecha(u.edited_at) })}</div>}

      {(u.next_step || u.omitted.length > 0) && (
        <div style={{ fontSize: 'var(--fs-2xs)' }}>
          <button className="btn ghost sm" style={{ fontSize: 'var(--fs-2xs)' }} onClick={() => setVerNotas((v) => !v)}>{verNotas ? '▾' : '▸'} {t('hou.staffNotes')}</button>
          {verNotas && (
            <div style={{ display: 'grid', gap: 4, padding: '6px 10px', color: 'var(--ink-2)' }}>
              {u.next_step && <div><b>{t('hou.nextStep')}:</b> {u.next_step}</div>}
              {u.omitted.map((o, i) => <div key={i}><b>{t('hou.omitted')}</b> {o.item}: {o.why}</div>)}
              {u.model && <div className="muted">{u.model} · {u.tokens_in ?? 0}/{u.tokens_out ?? 0} tokens</div>}
            </div>
          )}
        </div>
      )}

      <Rastreo u={u} t={t} />
      {verItems && <BucketModal u={u} t={t} onChanged={onChanged} onClose={() => setVerItems(false)} />}

      {msg && <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{msg}</div>}
      {!bloqueado && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          {editando && <button className="btn sm" disabled={busy != null} onClick={guardar}><Check size={13} strokeWidth={1.75} /> {t('common.save')}</button>}
          {editando && <button className="btn ghost sm" onClick={() => { setTexto(u.text_final ?? ''); setEditando(false); }}><X size={13} strokeWidth={1.75} /> {t('hou.cancel')}</button>}
          {!editando && u.status !== 'approved' && u.status !== 'skipped' && u.status !== 'failed' && (
            <button className="btn sm" disabled={busy != null} onClick={() => accion('approve')} style={{ background: 'var(--good-fill)', color: 'var(--good-fill-ink)', borderColor: 'transparent' }}><Check size={13} strokeWidth={1.75} /> {t('hou.approve')}</button>
          )}
          {!editando && u.status !== 'skipped' && <button className="btn ghost sm" disabled={busy != null} onClick={() => accion('skip')}><X size={13} strokeWidth={1.75} /> {t('hou.skip')}</button>}
          {!editando && u.status === 'skipped' && <button className="btn ghost sm" disabled={busy != null} onClick={() => accion('unskip')}><Play size={13} strokeWidth={1.75} /> {t('hou.unskip')}</button>}
          {!editando && <button className="btn ghost sm" disabled={busy != null} onClick={regenerar}><RefreshCw size={13} strokeWidth={1.75} /> {busy === 'regen' ? t('hou.generating') : t('hou.regenerate')}</button>}
          {!editando && hasRole(me, 'manager') && (u.status === 'draft' || u.status === 'approved') && (
            <button className="btn ghost sm" disabled={busy != null} onClick={enviarAhora}><Send size={13} strokeWidth={1.75} /> {busy === 'send' ? '…' : t('hou.sendNow')}</button>
          )}
        </div>
      )}
    </div>
  );
}

export function HoUpdatesTab({ claimId }: { claimId: string }) {
  const { t } = useI18n();
  const [data, setData] = useState<Resp | null>(null);
  const [bucket, setBucket] = useState<Bucket | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [nota, setNota] = useState('');
  const [motivoPausa, setMotivoPausa] = useState('');
  const [pausando, setPausando] = useState(false);
  const [abierto, setAbierto] = useState<string | null>(null);

  const cargar = useCallback(async () => {
    try {
      const [d, b] = await Promise.all([
        api.get<Resp>(`/api/claims/${claimId}/ho-updates`),
        api.get<Bucket>(`/api/claims/${claimId}/ho-updates/bucket`),
      ]);
      setData(d); setBucket(b); setError(null);
    } catch (e) { setError(e instanceof ApiError ? e.message : 'Error'); }
  }, [claimId]);
  useEffect(() => { cargar(); }, [cargar]);

  const porDia = useMemo(() => {
    const m = new Map<string, Item[]>();
    for (const it of bucket?.items ?? []) { const k = diaDe(it.at); (m.get(k) ?? m.set(k, []).get(k)!).push(it); }
    return [...m.entries()];
  }, [bucket]);

  async function toggleExclusion(it: Item) {
    try {
      await api.post(`/api/claims/${claimId}/ho-updates/exclusions`, { item_key: it.key, excluded: !it.excluded });
      setBucket((b) => b ? { ...b, items: b.items.map((x) => x.key === it.key ? { ...x, excluded: !x.excluded } : x) } : b);
    } catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); }
  }
  async function agregarNota() {
    if (!nota.trim()) return;
    setBusy('nota');
    try { await api.post(`/api/claims/${claimId}/ho-updates/notes`, { body: nota.trim() }); setNota(''); await cargar(); }
    catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); } finally { setBusy(null); }
  }
  async function borrarNota(id: string) {
    if (!window.confirm(t('common.confirmDelete'))) return;
    try { await api.del(`/api/ho-updates/notes/${id}`); await cargar(); } catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); }
  }
  async function generar() {
    setBusy('gen');
    try { await api.post(`/api/claims/${claimId}/ho-updates/generate`, { force: true }); await cargar(); }
    catch (e) { alert(e instanceof ApiError ? (e.code === 'claude_not_configured' ? t('hou.noClaude') : e.message) : 'Error'); } finally { setBusy(null); }
  }
  async function pausar(paused: boolean) {
    setBusy('pause');
    try { await api.post(`/api/claims/${claimId}/ho-updates/pause`, { paused, reason: motivoPausa || null }); setPausando(false); setMotivoPausa(''); await cargar(); }
    catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); } finally { setBusy(null); }
  }
  const onChanged = (nuevo: HoUpdate) => setData((d) => d ? { ...d, updates: d.updates.map((u) => u.id === nuevo.id ? nuevo : u) } : d);

  if (error) return <div className="card" style={{ color: 'var(--red)', fontSize: 'var(--fs-sm)' }}>{t('hou.loadError')}: {error}</div>;
  if (!data || !bucket) return <div className="muted" style={{ fontSize: 'var(--fs-xs)', padding: 12 }}>{t('common.loadingDots')}</div>;

  const modo = !data.config.enabled ? { label: t('hou.modeOff'), cls: 'gray' } : data.config.mode === 'dry_run' ? { label: t('hou.modeDryRun'), cls: 'orange' } : data.config.mode === 'pilot' ? { label: t('hou.modePilot'), cls: 'blue' } : { label: t('hou.modeLive'), cls: 'green' };
  const enCurso = data.updates.find((u) => u.period_key === data.config.period_key);
  const anteriores = data.updates.filter((u) => u.period_key !== data.config.period_key);
  const activos = bucket.items.filter((i) => !i.excluded).length;
  const infoClaim: ClaimInfo = { contractor: data.claim.contractor_name, status: data.claim.lifecycle_status, statusDays: data.claim.status_days };

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      {/* Cabecera: estado de la función, contacto, idioma, pausa. */}
      <div className="card" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <h2 className="card-title" style={{ margin: 0 }}><Mail size={15} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('hou.tab')}</h2>
        <span className={`badge ${modo.cls}`} style={{ fontSize: 'var(--fs-2xs)' }}>{modo.label}</span>
        <span className="badge gray" style={{ fontSize: 'var(--fs-2xs)' }}>{data.claim.language === 'es' ? 'Español' : 'English'}</span>
        <span className={`badge ${data.claim.homeowner_email ? 'gray' : 'red'}`} style={{ fontSize: 'var(--fs-2xs)' }}><Mail size={10} strokeWidth={1.75} /> {data.claim.homeowner_email ?? t('hou.noEmail')}</span>
        <span className={`badge ${data.claim.phone ? 'gray' : 'red'}`} style={{ fontSize: 'var(--fs-2xs)' }}><Phone size={10} strokeWidth={1.75} /> {data.claim.phone ?? t('hou.noPhone')}</span>
        <span style={{ flex: 1 }} />
        {data.claim.paused_at ? (
          <>
            <span className="badge orange" style={{ fontSize: 'var(--fs-2xs)' }}><Pause size={10} strokeWidth={1.75} /> {t('hou.pausedBy', { name: data.claim.paused_by ?? '' })}{data.claim.pause_reason ? ` · ${data.claim.pause_reason}` : ''}</span>
            <button className="btn ghost sm" disabled={busy != null} onClick={() => pausar(false)}><Play size={13} strokeWidth={1.75} /> {t('hou.resume')}</button>
          </>
        ) : pausando ? (
          <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
            <input className="input sm" placeholder={t('hou.pauseReason')} value={motivoPausa} onChange={(e) => setMotivoPausa(e.target.value)} style={{ width: 220 }} />
            <button className="btn sm" disabled={busy != null} onClick={() => pausar(true)}>{t('hou.pause')}</button>
            <button className="btn ghost sm" onClick={() => setPausando(false)}><X size={13} strokeWidth={1.75} /></button>
          </span>
        ) : (
          <button className="btn ghost sm" onClick={() => setPausando(true)}><Pause size={13} strokeWidth={1.75} /> {t('hou.pause')}</button>
        )}
      </div>

      {/* La semana en curso: el bucket en vivo + la nota. */}
      <div className="card" style={{ display: 'grid', gap: 10 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <b style={{ fontSize: 'var(--fs-sm)' }}>{t('hou.thisWeek')}</b>
          <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{fecha(bucket.desde)} → {t('hou.now')} · {t('hou.cutOn', { date: fecha(bucket.period_key, false) })}</span>
          <span className="badge gray" style={{ fontSize: 'var(--fs-2xs)' }}>{t('hou.itemsCount', { n: activos })}</span>
          <span style={{ flex: 1 }} />
          <button className="btn sm" disabled={busy != null} onClick={generar}><Sparkles size={13} strokeWidth={1.75} /> {busy === 'gen' ? t('hou.generating') : (enCurso ? t('hou.regenerateDraft') : t('hou.generateDraft'))}</button>
        </div>
        <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>
          {t('hou.stageNow')}: {bucket.hechos.stage_phrase}{bucket.hechos.waiting_on ? ` · ${t('hou.waitingOn')}: ${bucket.hechos.waiting_on}` : ''}{bucket.hechos.next_appointment ? ` · ${t('hou.nextVisit')}: ${bucket.hechos.next_appointment}` : ''}
        </div>

        {/* Nota para el update. */}
        <div style={{ display: 'grid', gap: 6, background: 'var(--accent-soft)', borderRadius: 9, padding: '8px 10px' }}>
          <b style={{ fontSize: 'var(--fs-2xs)', color: 'var(--accent)' }}><StickyNote size={11} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('hou.noteTitle')}</b>
          {bucket.notas.map((n) => (
            <div key={n.id} style={{ display: 'flex', gap: 6, alignItems: 'start', fontSize: 'var(--fs-xs)' }}>
              <span style={{ flex: 1 }}>{n.body} <span className="muted">· {n.author ?? ''} · {fecha(n.created_at)}</span></span>
              <button className="btn ghost sm" onClick={() => borrarNota(n.id)} title={t('hou.deleteNote')}><Trash2 size={12} strokeWidth={1.75} /></button>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 6 }}>
            <input className="input sm" style={{ flex: 1 }} placeholder={t('hou.notePh')} value={nota} onChange={(e) => setNota(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') agregarNota(); }} />
            <button className="btn sm" disabled={busy != null || !nota.trim()} onClick={agregarNota}>{t('hou.addNote')}</button>
          </div>
        </div>

        {/* El bucket. */}
        {porDia.length === 0 ? (
          <div className="muted" style={{ fontSize: 'var(--fs-xs)', padding: 10, textAlign: 'center', border: '1px dashed var(--line)', borderRadius: 9 }}>{t('hou.emptyWeek')}</div>
        ) : porDia.map(([dia, items]) => (
          <div key={dia} style={{ display: 'grid', gap: 4 }}>
            <div className="muted" style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-semibold)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{dia}</div>
            {items.map((it) => {
              const Ic = ICONO[it.kind] ?? FileText;
              const open = abierto === it.key;
              return (
                <div key={it.key} style={{ display: 'flex', gap: 8, alignItems: 'start', padding: '5px 8px', borderRadius: 8, background: it.excluded ? 'transparent' : 'var(--card)', border: '1px solid var(--line)', opacity: it.excluded ? 0.5 : 1 }}>
                  <span style={{ color: COLOR[it.kind] ?? 'var(--muted)', paddingTop: 2 }}><Ic size={13} strokeWidth={1.75} /></span>
                  <div style={{ flex: 1, minWidth: 0, fontSize: 'var(--fs-xs)', cursor: 'pointer' }} onClick={() => setAbierto(open ? null : it.key)}>
                    <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>
                      {fecha(it.at)} · {it.kind === 'msg' ? `${it.dept ?? 'General'} · ` : ''}{it.who_name ?? t(`hou.who_${it.who}`)}{it.direction ? (it.direction === 'in' ? ' ↙' : ' ↗') : ''}
                    </span>
                    {it.title && <div style={{ fontWeight: 'var(--fw-semibold)' }}>{it.title}</div>}
                    <div style={{ whiteSpace: open ? 'pre-wrap' : 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textDecoration: it.excluded ? 'line-through' : 'none' }}>{it.text}</div>
                  </div>
                  <button className="btn ghost sm" title={it.excluded ? t('hou.include') : t('hou.exclude')} onClick={() => toggleExclusion(it)}>
                    {it.excluded ? <EyeOff size={13} strokeWidth={1.75} /> : <Eye size={13} strokeWidth={1.75} />}
                  </button>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* El update de esta semana (si ya se generó) y los anteriores. */}
      {enCurso && <UpdateCard u={enCurso} t={t} onChanged={onChanged} claim={infoClaim} />}
      {anteriores.length > 0 && <div className="muted" style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-semibold)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{t('hou.previous')}</div>}
      {anteriores.map((u) => <UpdateCard key={u.id} u={u} t={t} onChanged={onChanged} claim={infoClaim} />)}
      {!enCurso && anteriores.length === 0 && <div className="muted" style={{ fontSize: 'var(--fs-xs)' }}>{t('hou.noUpdatesYet')}</div>}
      {data.link && !data.link.revoked && (
        <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>
          {t('hou.publicLink')}: <a href={`/p/update/${data.link.token}`} target="_blank" rel="noreferrer">/p/update/{data.link.token.slice(0, 8)}…</a> · {t('hou.linkHits', { n: data.link.hits })}{data.link.last_seen_at ? ` · ${fecha(data.link.last_seen_at)}` : ''}
        </div>
      )}
    </div>
  );
}
